import { useState, useEffect, useCallback, useRef } from "react";
import useFocoModal from "../../hooks/useFocoModal";
import { getNotionItems, setNotionItemEnabled, integrationConnectUrl, getNotionConfig } from "../../api";

// ── Navegador del workspace de Notion + gestión de páginas enlazadas ─────────
// El árbol se arma con `parent_external_id`, que la sincronización rellena con
// el padre real. Las páginas que son FILAS de una database no salen en el árbol:
// esas se ven dentro de su database, no como nodos sueltos.

const isEmoji = (str) => str && /\p{Emoji}/u.test(str) && str.length <= 4;

const IconDb = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="var(--text3)">
    <ellipse cx="8" cy="4" rx="6" ry="2.5" />
    <path d="M2 4v7.5C2 12.88 4.69 14 8 14s6-1.12 6-2.5V4" opacity="0.45" />
  </svg>
);
const IconDoc = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="var(--text3)" strokeWidth="1.4">
    <path d="M4 2h5l3 3v9H4z" /><path d="M9 2v3h3" />
  </svg>
);

// Construye el árbol y devuelve los nodos raíz
export function construirArbol(items = []) {
  const porId = new Map(items.map(i => [i.external_id, i]));
  const esFilaDeDatabase = (it) =>
    it.item_type === "page" && porId.get(it.parent_external_id)?.item_type === "database";

  const nodos = items.filter(i => !esFilaDeDatabase(i));
  const mapa  = new Map(nodos.map(n => [n.external_id, { ...n, hijos: [] }]));
  const raices = [];

  for (const nodo of mapa.values()) {
    const padre = nodo.parent_external_id ? mapa.get(nodo.parent_external_id) : null;
    if (padre) padre.hijos.push(nodo);
    else raices.push(nodo);       // sin padre, o con un padre al que no tenemos acceso
  }

  const ordenar = (lista) => {
    lista.sort((a, b) => (a.item_type === b.item_type
      ? (a.title || "").localeCompare(b.title || "")
      : a.item_type === "database" ? -1 : 1));      // las databases primero
    lista.forEach(n => ordenar(n.hijos));
    return lista;
  };
  return ordenar(raices);
}

// Árbol operable con teclado (patrón WAI-ARIA tree, lista plana con aria-level):
// un solo nodo entra en el orden de tabulación (tabIndex 0) y las flechas
// mueven entre los visibles. Derecha abre o baja al primer hijo, Izquierda
// cierra o sube al padre, Inicio/Fin saltan a los extremos, Intro/Espacio elige.
function Nodo({ nodo, nivel, activeId, onSelect, contar, focoId, setFocoId, primeroId, posicion, tamano }) {
  const [abierto, setAbierto] = useState(nivel === 0);
  const activo = nodo.external_id === activeId;
  const hijos  = nodo.hijos || [];
  const n      = nodo.item_type === "database" ? contar(nodo.external_id) : null;
  const tieneFoco = nodo.external_id === (focoId ?? primeroId);

  const activar = () => { onSelect(nodo); if (hijos.length) setAbierto(a => !a); };

  const onKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    const arbol = e.currentTarget.closest("[role=tree]");
    const items = arbol ? [...arbol.querySelectorAll("[role=treeitem]")] : [];
    const i = items.indexOf(e.currentTarget);
    const ir = (el) => { if (el) { e.preventDefault(); el.focus(); } };
    switch (e.key) {
      case "Enter": case " ": e.preventDefault(); activar(); break;
      case "ArrowDown": ir(items[i + 1]); break;
      case "ArrowUp": ir(items[i - 1]); break;
      case "Home": ir(items[0]); break;
      case "End": ir(items[items.length - 1]); break;
      case "ArrowRight":
        if (hijos.length && !abierto) { e.preventDefault(); setAbierto(true); }
        else if (hijos.length && abierto) ir(items[i + 1]);
        break;
      case "ArrowLeft":
        if (hijos.length && abierto) { e.preventDefault(); setAbierto(false); }
        else {
          const nv = Number(e.currentTarget.getAttribute("aria-level"));
          for (let j = i - 1; j >= 0; j--) {
            if (Number(items[j].getAttribute("aria-level")) < nv) { ir(items[j]); break; }
          }
        }
        break;
      default:
    }
  };

  return (
    <>
      <div
        role="treeitem"
        aria-level={nivel + 1}
        aria-posinset={posicion}
        aria-setsize={tamano}
        aria-selected={activo}
        aria-expanded={hijos.length ? abierto : undefined}
        aria-label={(nodo.title || "Sin título") + (n ? `, ${n} páginas` : "")}
        tabIndex={tieneFoco ? 0 : -1}
        onFocus={() => setFocoId(nodo.external_id)}
        onClick={activar}
        onKeyDown={onKeyDown}
        style={{
          display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
          padding: "5px 8px", paddingLeft: 8 + nivel * 13, borderRadius: 7,
          background: activo ? "var(--accent-bg)" : "transparent",
          color: activo ? "var(--accent)" : "var(--text2)",
          fontSize: 12.5, fontWeight: activo ? 700 : 500,
        }}
        onMouseEnter={e => { if (!activo) e.currentTarget.style.background = "var(--bg)"; }}
        onMouseLeave={e => { if (!activo) e.currentTarget.style.background = "transparent"; }}>
        <span aria-hidden="true" style={{ width: 10, flexShrink: 0, fontSize: 11, color: "var(--text3)" }}>
          {hijos.length ? (abierto ? "▾" : "▸") : ""}
        </span>
        <span aria-hidden="true" style={{ display: "inline-flex", flexShrink: 0 }}>
          {isEmoji(nodo.icon)
            ? <span style={{ fontSize: 13 }}>{nodo.icon}</span>
            : nodo.item_type === "database" ? <IconDb /> : <IconDoc />}
        </span>
        <span aria-hidden="true" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {nodo.title || "Sin título"}
        </span>
        {n != null && n > 0 && (
          <span aria-hidden="true" style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)" }}>{n}</span>
        )}
      </div>
      {abierto && hijos.map((h, k) => (
        <Nodo key={h.external_id} nodo={h} nivel={nivel + 1} activeId={activeId} onSelect={onSelect} contar={contar}
          focoId={focoId} setFocoId={setFocoId} primeroId={primeroId} posicion={k + 1} tamano={hijos.length} />
      ))}
    </>
  );
}

export default function NotionNav({ items, activeId, onSelect }) {
  const raices = construirArbol(items);
  const contar = (dbId) => items.filter(i => i.item_type === "page" && i.parent_external_id === dbId).length;
  const [focoId, setFocoId] = useState(null);
  const primeroId = raices[0]?.external_id;

  return (
    <div className="nt-nav" style={{ width: 232, flexShrink: 0, borderRight: "1px solid var(--border)", paddingRight: 10, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 8px 8px" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".06em" }}>Workspace</span>
      </div>
      {raices.length === 0
        ? <div style={{ flex: 1, overflowY: "auto", fontSize: 12, color: "var(--text3)", padding: "10px 8px" }}>Nada sincronizado todavía.</div>
        : (
          <div role="tree" aria-label="Workspace de Notion" style={{ flex: 1, overflowY: "auto" }}>
            {raices.map((n, k) => <Nodo key={n.external_id} nodo={n} nivel={0} activeId={activeId} onSelect={onSelect} contar={contar}
              focoId={focoId} setFocoId={setFocoId} primeroId={primeroId} posicion={k + 1} tamano={raices.length} />)}
          </div>
        )}
    </div>
  );
}

// ── Gestión de páginas: enlazar, desenlazar y diagnosticar ───────────────────
//
// Hay DOS formas de que Notion nos deje ver una página, y no son intercambiables:
//
//   1. Desde Notion, añadiendo la conexión en la propia página (••• → Conexiones).
//      Es la fiable: no pasa por OAuth, no depende de la sesión del navegador y
//      permite añadir de una en una sin tocar lo ya concedido.
//   2. Volviendo a pasar por el consentimiento de OAuth, que abre el selector de
//      Notion. Es el que se queda dando vueltas cuando la sesión de Notion del
//      navegador está a medias, y ademas SUSTITUYE lo concedido por lo que
//      marques: desmarcar ahí es perder acceso a esas páginas.
//
// Desenlazar de verdad (quitarle el acceso a ALM) solo se puede hacer en Notion:
// la API no tiene forma de renunciar al permiso sobre una página suelta. Lo que
// sí es nuestro es dejar de mostrarla, que es la casilla de cada fila.
export function NotionPagesManager({ token, onClose, onChanged, onSync, onNotify }) {
  const [todos, setTodos]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState(null);
  const [busy, setBusy]         = useState(null);
  const [cfg, setCfg]           = useState(null);
  const [verCfg, setVerCfg]     = useState(false);
  // Dialogo modal: foco dentro, Tab atrapado, Escape cierra y el foco vuelve al
  // boton «Gestionar» que lo abrio.
  const panelRef = useRef(null);
  useFocoModal(panelRef, true, onClose);

  // La URL de autorizacion se pide con la sesion en la cabecera. La pestana se
  // abre ANTES, en el gesto del clic, porque tras un await el navegador la
  // tomaria por una ventana emergente y la bloquearia.
  const abrirSelectorNotion = async () => {
    const pestana = window.open("", "_blank");
    try {
      const url = await integrationConnectUrl("notion");
      if (pestana) pestana.location.href = url; else window.location.href = url;
    } catch (e) {
      if (pestana) pestana.close();
      setError(e.message || "No se pudo abrir el selector de Notion");
    }
  };
  const [sincronizando, setSincronizando] = useState(false);

  const cargar = useCallback(() => {
    setCargando(true); setError(null);
    return getNotionItems({ todo: true })
      .then(d => setTodos(Array.isArray(d) ? d : []))
      .catch(e => setError(e.message || "No se pudieron cargar las páginas"))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    cargar();
    getNotionConfig().then(setCfg).catch(() => {});
  }, [cargar]);

  // Solo lo "enlazable": databases y páginas sueltas. Las filas de una database
  // van con su database, no se conectan una a una.
  const porId = new Map(todos.map(i => [i.external_id, i]));
  const enlazables = todos.filter(i =>
    i.item_type === "database" ||
    porId.get(i.parent_external_id)?.item_type !== "database");

  const toggle = async (item) => {
    setBusy(item.external_id);
    try {
      const enabled = !(item.enabled !== false);
      await setNotionItemEnabled(item.external_id, enabled);
      setTodos(list => list.map(x => (x.external_id === item.external_id ? { ...x, enabled } : x)));
      onChanged?.();
    } catch (e) {
      onNotify?.(e.message || "No se pudo cambiar", "warn");
    } finally {
      setBusy(null);
    }
  };

  const sincronizar = async () => {
    setSincronizando(true);
    try { await onSync?.(); await cargar(); } finally { setSincronizando(false); }
  };

  // Si esto está mal, el consentimiento de Notion no puede funcionar y no tiene
  // sentido que el usuario siga intentándolo.
  const cfgRota = cfg && (!cfg.client_id || /localhost|127\.0\.0\.1/.test(cfg.redirect_uri || ""));
  const conectadas = enlazables.filter(i => i.enabled !== false).length;

  const secS  = { border: "1px solid var(--border)", borderRadius: 10, padding: "12px 14px", marginBottom: 12 };
  const tituS = { fontSize: 12.5, fontWeight: 700, color: "var(--text)", marginBottom: 6 };
  const txtS  = { fontSize: 12, color: "var(--text3)", lineHeight: 1.65 };
  const btnS  = {
    fontSize: 12, fontWeight: 600, padding: "7px 13px", borderRadius: 8, cursor: "pointer",
    border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text2)",
    fontFamily: "var(--font)",
  };

  return (
    <>
      <div onClick={onClose} aria-hidden="true" style={{ position: "fixed", inset: 0, zIndex: 320, background: "rgba(0,0,0,0.18)" }} />
      <div ref={panelRef} role="dialog" aria-modal="true" style={{
        position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 321,
        width: "min(540px, 96vw)", background: "var(--surface)",
        borderLeft: "1px solid var(--border)", boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div data-titulo style={{ fontWeight: 800, fontSize: 15, color: "var(--text)" }}>Elegir qué páginas mostrar</div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>
              {conectadas} de {enlazables.length} visibles en ALM
            </div>
          </div>
          <button onClick={sincronizar} disabled={sincronizando} style={btnS}>
            {sincronizando ? "Sincronizando…" : "Sincronizar"}
          </button>
          <button onClick={onClose} aria-label="Cerrar" style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 16, color: "var(--text3)" }}><span aria-hidden="true">✕</span></button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "14px 20px" }}>

          {/* ── Camino 1: desde Notion ───────────────────────────────────── */}
          <div style={{ ...secS, borderColor: "var(--accent)", background: "var(--accent-bg)" }}>
            <div style={tituS}>Traer una página nueva · desde Notion</div>
            <div style={txtS}>
              En Notion, abre la página o base de datos y pulsa <b> ••• </b>(arriba a la
              derecha) → <b>Conexiones</b> → <b>Conectar a</b> → elige la integración de ALM.
              Vuelve aquí y pulsa <b>Sincronizar</b>.
            </div>
            <div style={{ ...txtS, marginTop: 8, border: "1px solid var(--border)", borderRadius: 8, padding: "7px 10px" }}>
              <b>¿No te aparece «Conexiones» en ese menú?</b> Entonces en ese espacio eres
              invitado, no miembro (lo pone junto al nombre del espacio, en la barra lateral de
              Notion). Un invitado no puede darle acceso a una integración: tiene que hacerlo un
              miembro del espacio desde esa misma página, o pedir que te pasen a miembro. Por el
              mismo motivo, el selector de permisos de abajo tampoco te dejará añadirla.
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <a href="https://www.notion.so" target="_blank" rel="noopener noreferrer"
                style={{ ...btnS, textDecoration: "none", display: "inline-block" }}>Abrir Notion ↗</a>
              <button onClick={sincronizar} disabled={sincronizando} style={{ ...btnS, background: "var(--surface)" }}>
                {sincronizando ? "Sincronizando…" : "Ya está: sincronizar"}
              </button>
            </div>
          </div>

          {/* ── Camino 2: OAuth ──────────────────────────────────────────── */}
          <div style={secS}>
            <div style={tituS}>O volver a pedir permisos a Notion</div>
            <div style={txtS}>
              Abre el selector de Notion en otra pestaña. Ojo: Notion <b>sustituye</b> lo
              concedido por lo que dejes marcado, así que no desmarques lo que ya usas. Si se
              queda dando vueltas en <code>app.notion.com</code>, es la sesión de Notion de
              este navegador: entra primero en Notion, cierra las pestañas de login a medias y
              vuelve a intentarlo.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button onClick={abrirSelectorNotion} style={{ border: "none", cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none",
                background: "#000", color: "#fff", borderRadius: 8, padding: "8px 14px",
                fontSize: 12, fontWeight: 600,
              }}>
                <span style={{ fontFamily: "Georgia, serif", fontWeight: 900 }}>N</span>
                Abrir el selector de Notion
              </button>
              <button onClick={() => setVerCfg(v => !v)} style={{ ...btnS, border: "none", color: "var(--text3)" }}>
                {verCfg ? "Ocultar configuración" : "Ver configuración"}
              </button>
            </div>

            {cfgRota && (
              <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.6, color: "#dc2626", background: "var(--red-bg)", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 11px" }}>
                <b>La configuración del servidor está mal, por eso no funciona.</b>{" "}
                {!cfg.client_id
                  ? "Al backend le falta NOTION_CLIENT_ID."
                  : "El redirect_uri apunta a localhost: falta BACKEND_URL en el backend."}
              </div>
            )}

            {verCfg && cfg && (
              <div style={{ marginTop: 10, fontSize: 11.5, lineHeight: 1.7, color: "var(--text3)", fontFamily: "monospace", wordBreak: "break-all" }}>
                <div>client_id: {cfg.client_id || <b style={{ color: "#dc2626" }}>FALTA</b>}</div>
                <div>redirect_uri: {cfg.redirect_uri}</div>
                <div style={{ fontFamily: "var(--font)", marginTop: 6 }}>
                  Ese <b>redirect_uri</b> tiene que estar, tal cual, en las Redirect URIs de la
                  integración en notion.so/my-integrations.
                </div>
              </div>
            )}
          </div>

          {/* ── Lo que hay ───────────────────────────────────────────────── */}
          <div style={{ ...tituS, marginTop: 16, marginBottom: 8 }}>Enlazado ahora mismo</div>

          {error ? (
            <div style={{ fontSize: 12.5, color: "#dc2626", background: "var(--red-bg)", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 11px" }}>
              {error}
            </div>
          ) : cargando ? (
            <div style={{ fontSize: 12.5, color: "var(--text3)" }}>Cargando…</div>
          ) : enlazables.length === 0 ? (
            <div style={{ fontSize: 12.5, color: "var(--text3)" }}>Nada sincronizado todavía.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {enlazables.map(item => {
                const on = item.enabled !== false;
                return (
                  <div key={item.external_id} style={{
                    display: "flex", alignItems: "center", gap: 9, padding: "8px 10px",
                    borderRadius: 8, border: "1px solid var(--border)",
                    background: on ? "var(--surface)" : "var(--bg)",
                    opacity: busy === item.external_id ? 0.5 : 1,
                  }}>
                    <input type="checkbox" checked={on} disabled={busy === item.external_id}
                      onChange={() => toggle(item)}
                      aria-label={`Mostrar «${item.title || "Sin título"}» en ALM`}
                      title={on ? "Dejar de mostrarla en ALM" : "Volver a mostrarla en ALM"}
                      style={{ accentColor: "var(--accent)", width: 14, height: 14, flexShrink: 0, cursor: "pointer" }} />
                    {isEmoji(item.icon)
                      ? <span style={{ fontSize: 14 }}>{item.icon}</span>
                      : item.item_type === "database" ? <IconDb /> : <IconDoc />}
                    <span style={{ flex: 1, fontSize: 12.5, fontWeight: on ? 600 : 400, color: on ? "var(--text)" : "var(--text3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.title || "Sin título"}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--text3)", flexShrink: 0 }}>
                      {item.item_type === "database" ? "Base de datos" : "Página"}
                    </span>
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noopener noreferrer" title="Abrir en Notion para quitarle la conexión"
                        style={{ fontSize: 11, color: "var(--accent)", textDecoration: "none", flexShrink: 0 }}>Notion ↗</a>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ ...txtS, marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <b>Desmarcar</b> deja de mostrarla en ALM, pero seguimos teniendo acceso en Notion.
            Para <b>desenlazarla de verdad</b>, abre la página en Notion (enlace <b>Notion ↗</b>) y
            en <b>••• → Conexiones</b> quita la de ALM; luego <b>Sincronizar</b> aquí y desaparece
            de esta lista. La API de Notion no permite renunciar al permiso desde fuera.
          </div>
        </div>
      </div>
    </>
  );
}
